import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListAccessKeys } from 'src/features/accessKey/api/useListAccessKeys';
import EntityFormField from 'src/features/membership/components/forms/EntityFormField';
import { IDENTIFIER_TYPE } from 'src/features/membership/types';
import { IDENTIFIER_TYPE_TRANSLATION_MAP } from 'src/features/membership/utils/constants';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { MembershipIdentifierFormSchemaValues } from 'src/features/membershipType/formSchema';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { transliterateUppercase } from 'src/utils/regex';

type IdentifierFormProps = {
    form: UseFormReturn<MembershipIdentifierFormSchemaValues>;
    onSubmit: (values: MembershipIdentifierFormSchemaValues) => void;
    isClient?: boolean;
};
function IdentifierForm({ onSubmit, form, isClient = false }: IdentifierFormProps) {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { data } = useListAccessKeys({ parkingId, config: { enabled: !isClient } });
    const { identifier } = form.watch();

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {!isClient && (
                <SelectFormField<MembershipIdentifierFormSchemaValues>
                    form={form}
                    name="identifier.type"
                    label={formatMessage(identifiersMessages.identifierType)}
                    options={[IDENTIFIER_TYPE.LICENSE_PLATE, IDENTIFIER_TYPE.ACCESS_KEY]}
                    optionTranslationMap={IDENTIFIER_TYPE_TRANSLATION_MAP}
                    onChange={() => form.setValue('identifier.value', '')}
                />
            )}

            {identifier?.type === IDENTIFIER_TYPE.ACCESS_KEY ? (
                <FormField
                    control={form.control}
                    name="identifier.value"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...identifiersMessages.accessKey} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue
                                                placeholder={`${formatMessage(identifiersMessages.accessKey)}...`}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {data?.results.map((option) => (
                                                <SelectItem key={option.id} value={option.value}>
                                                    {option.value}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            ) : (
                <FormField
                    control={form.control}
                    name="identifier.value"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(identifiersMessages.licensePlate)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        {...field}
                                        onChange={(e) => {
                                            const value = transliterateUppercase(e.target.value.toLocaleUpperCase());
                                            field.onChange(value);
                                        }}
                                        value={field.value ?? ''}
                                        placeholder={`${formatMessage(identifiersMessages.licensePlate)}...`}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}

            {!isClient && (
                <>
                    <InputFormField<MembershipIdentifierFormSchemaValues>
                        required={false}
                        form={form}
                        name="public_notes"
                        label={formatMessage(identifiersMessages.publicNotes)}
                    />
                    <InputFormField<MembershipIdentifierFormSchemaValues>
                        required={false}
                        form={form}
                        name="private_notes"
                        label={formatMessage(identifiersMessages.privateNotes)}
                    />
                    <EntityFormField form={form} />
                </>
            )}
        </Form>
    );
}

export default IdentifierForm;
